import { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import { Form, Formik, Field } from 'formik';

import {
    IUpdateScormInput,
    IScormEditFragment,
    IScormListItemFragment,
    IBasicFileFragment,
    IFileFragment,
} from 'graphql/types';
import { TextField, FileField } from 'common/components/FormField';
import { ExitPageAlert } from 'common/components/ExitPageAlert';
import { SCORM_FILE_MAX_SIZE, SCORM_FILE_TYPES } from 'common/constants/files';
import { ContentExpander } from 'common/components/ContentExpander';
import { FormSection } from 'common/components/FormSection';
import { Link } from 'common/components/Link';
import { UnsavedChangesAlert } from 'common/components/Alerts';
import { Button } from 'common/components/Button';
import { FormDeleteButton } from 'common/components/Button/FormDeleteButton';
import { Divider } from 'common/components/Divider';
import { ScormListItem } from 'training/components/ScormListItem';
import { ScormPreviewModal } from 'training/components/ScormPreviewModal';
import { ScormImageFormSection } from 'training/components/forms/ScormForm/ScormImageFormSection';

export type TScormFormValues = Omit<IUpdateScormInput, 'thumbnailId'> & {
    fileUrl: string;
    zipFile?: IFileFragment | IBasicFileFragment;
    thumbnail?: IFileFragment | IBasicFileFragment;
};

export interface IProps {
    fileInvalidError?: boolean;
    edit?: boolean;
    initialValues: TScormFormValues;
    scorm?: IScormEditFragment;
    disabled?: boolean;
    onClose?(): void;
    onSubmit(values: TScormFormValues): void;
    onDelete?(): void;
    onReplaceScorm?(): void;
    setFileInvalidError?(hasError: boolean): void;
}

export const ScormForm = ({
    fileInvalidError,
    edit,
    initialValues,
    onSubmit,
    disabled,
    scorm,
    onClose,
    onDelete,
    setFileInvalidError,
}: IProps) => {
    const [translate] = useTranslation();
    const [settingsExpanded, setSettingsExpanded] = useState(false);
    const [showPreview, setShowPreview] = useState(false);

    const fileField = (
        <>
            <Field
                acceptedFileTypes={SCORM_FILE_TYPES}
                component={FileField}
                helperText={translate(
                    !!scorm
                        ? 'scormForm.fileHelperTextReplace'
                        : 'scormForm.fileHelperText',
                    {
                        count: SCORM_FILE_MAX_SIZE,
                    }
                )}
                label={translate('scormForm.file')}
                maxUploadSize={SCORM_FILE_MAX_SIZE}
                name="zipFile"
                onChange={() => {
                    // Reset file invalid error when file is changed
                    setFileInvalidError?.(false);
                }}
            />

            {fileInvalidError && (
                <FormHelperText error>
                    {translate('scormForm.scormFileInvalid')}
                </FormHelperText>
            )}
        </>
    );

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit}>
            {({ submitForm, isSubmitting, dirty }) => (
                <Box sx={{ p: { xs: 2, sm: 4 } }}>
                    <ExitPageAlert
                        alert={UnsavedChangesAlert}
                        when={dirty && !isSubmitting}
                        onConfirm={submitForm}
                    />

                    <Form>
                        {!!scorm ? (
                            <ScormListItem
                                actionText={translate('preview')}
                                px={0}
                                py={0}
                                scorm={scorm as IScormListItemFragment}
                                onClick={() => setShowPreview(true)}
                            />
                        ) : (
                            fileField
                        )}

                        {!!scorm && (
                            <ScormPreviewModal
                                open={showPreview}
                                scormId={scorm.id}
                                title={scorm.title}
                                onClose={() => setShowPreview(false)}
                            />
                        )}

                        <Box sx={{ mt: { xs: 2, sm: 4 }, mb: 2 }}>
                            <Divider />
                        </Box>

                        <Field
                            component={TextField}
                            label={translate('title')}
                            name="title"
                        />

                        <Box sx={{ mt: 4 }}>
                            <ContentExpander
                                expand={settingsExpanded}
                                title={translate('moreSettings')}
                                onClick={() =>
                                    setSettingsExpanded(!settingsExpanded)
                                }
                            >
                                {!!scorm && (
                                    <FormSection
                                        description={
                                            <Trans
                                                components={{
                                                    a: (
                                                        <Link
                                                            rel="noreferrer"
                                                            underline="always"
                                                        />
                                                    ),
                                                }}
                                                i18nKey="scormForm.scormFileDescription"
                                            />
                                        }
                                        title={translate('scormForm.scormFile')}
                                    >
                                        <Box sx={{ mt: 3 }}>{fileField}</Box>
                                    </FormSection>
                                )}

                                <ScormImageFormSection />
                            </ContentExpander>
                        </Box>

                        <Box sx={{ display: 'flex', mt: 4 }}>
                            <Box>
                                <Button
                                    color="primary"
                                    disabled={
                                        isSubmitting ||
                                        disabled ||
                                        (!isSubmitting && !disabled && !dirty)
                                    }
                                    loading={isSubmitting}
                                    type="submit"
                                    variant="contained"
                                    onClick={(
                                        e: React.MouseEvent<HTMLButtonElement>
                                    ) => {
                                        e.preventDefault();

                                        return submitForm();
                                    }}
                                >
                                    {translate(
                                        `scormForm.${edit ? 'editButton' : 'createButton'}`
                                    )}
                                </Button>
                            </Box>

                            {onClose && (
                                <Box sx={{ ml: 2 }}>
                                    <Button
                                        disabled={isSubmitting}
                                        variant="contained"
                                        onClick={onClose}
                                    >
                                        {translate('cancel')}
                                    </Button>
                                </Box>
                            )}

                            {onDelete && edit && (
                                <Box sx={{ ml: 'auto' }}>
                                    <FormDeleteButton
                                        disabled={isSubmitting || disabled}
                                        onClick={onDelete}
                                    >
                                        {translate('delete')}
                                    </FormDeleteButton>
                                </Box>
                            )}
                        </Box>
                    </Form>
                </Box>
            )}
        </Formik>
    );
};
